import { useRef, useEffect, useState, useCallback } from "react";

import { calculateVideoLayout, VideoLayout } from "../../utils/videoLayout";

interface ContainerSize {
  width: number;
  height: number;
}

const EMPTY_LAYOUT: VideoLayout = { width: 0, height: 0, offsetX: 0, offsetY: 0 };
const EMPTY_SIZE: ContainerSize = { width: 0, height: 0 };

export const useVideoLayout = (containerRef: React.RefObject<HTMLDivElement | null>) => {
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const [containerSize, setContainerSize] = useState<ContainerSize>(EMPTY_SIZE);
  const [videoLayout, setVideoLayout] = useState<VideoLayout>(EMPTY_LAYOUT);

  const updateLayout = useCallback((containerWidth: number, containerHeight: number) => {
    const videoEl = videoElementRef.current;
    if (!videoEl) return;

    const { videoWidth, videoHeight } = videoEl;
    if (containerWidth > 0 && containerHeight > 0 && videoWidth > 0 && videoHeight > 0) {
      setVideoLayout(calculateVideoLayout(containerWidth, containerHeight, videoWidth, videoHeight));
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const findVideoElement = (): HTMLVideoElement | null => {
      const parent = container.parentElement;
      return parent?.querySelector("video") ?? null;
    };

    const setupVideoElement = () => {
      const videoEl = findVideoElement();
      if (!videoEl) return;

      videoElementRef.current = videoEl;

      const handleLoadedMetadata = () => {
        const { width, height } = container.getBoundingClientRect();
        updateLayout(width, height);
      };

      if (videoEl.readyState >= 1) {
        handleLoadedMetadata();
      }

      videoEl.addEventListener("loadedmetadata", handleLoadedMetadata);
      return () => {
        videoEl.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    };

    const cleanup = setupVideoElement();

    // Retry in case the <video> element mounts after us. Cleanup is captured separately so its listener isn't leaked.
    let retryCleanup: (() => void) | undefined;
    const timeoutId = setTimeout(() => {
      if (!videoElementRef.current) {
        retryCleanup = setupVideoElement();
      }
    }, 100);

    return () => {
      cleanup?.();
      retryCleanup?.();
      clearTimeout(timeoutId);
    };
  }, [containerRef, updateLayout]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let prevWidth = 0;
    let prevHeight = 0;
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        if (width === prevWidth && height === prevHeight) return;
        prevWidth = width;
        prevHeight = height;
        setContainerSize({ width, height });
        updateLayout(width, height);
      }
    });

    resizeObserver.observe(container);

    const { width, height } = container.getBoundingClientRect();
    setContainerSize({ width, height });
    updateLayout(width, height);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, updateLayout]);

  return { containerSize, videoLayout, videoElementRef };
};
